import { ConfigProvider, Form, Input, message, Modal, Select, InputNumber } from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Province } from "@/core/models/address/province.model";
import { District } from "@/core/models/address/district.model";
import { Ward } from "@/core/models/address/ward.model";
import container from "@/lib/di/container";
import { AddressRepo } from "@/core/repos/address.repo";
import { PaymentPointRepo } from "@/core/repos/tables/payment-point.repo";
import React from "react";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import { usePermissionStore } from "@/core/stores/permission.store";
import { SaveButton } from "@/app/home/components/table/SaveButton";
import { PaymentPointModel } from "@/core/models/payment-point.model";

const addressRepo = container.get(AddressRepo);
const paymentPointRepo = container.get(PaymentPointRepo);

interface DetailModalProps {
    isModalOpen: boolean,
    setIsModalOpen: Dispatch<SetStateAction<boolean>>,
    data: PaymentPointModel,
}

const INPUT_WIDTH = 450;

export const DetailModal: React.FC<DetailModalProps> = ({isModalOpen, setIsModalOpen, data}) => {
    const permissionStore = usePermissionStore();
    const [form] = Form.useForm<PaymentPointModel>();
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [provinces, setProvinces] = useState<Province[]>([]);
    const [districts, setDistricts] = useState<District[]>([]);
    const [wards, setWards] = useState<Ward[]>([]);
    const [messageApi, contextHolder] = message.useMessage();

    // Attach hooks to handle data changes
    const handleDataChange = async () => {
        await handleProvinceChange(data.provinceId || "");
        await handleDistrictChange(data.districtId || "");
        form.setFieldsValue(data);
        if (data.id && data.id.length > 0) {
            setIsEdit(true);
        } else {
            setIsEdit(false);
        }
    };

    useEffect(() => {
        handleDataChange();
    }, [data]);

    // Initialize select options data
    const populateProvinces = async () => {
        const _provinces = (await addressRepo.getAllProvinces()).map(data => data[1]);
        setProvinces(_provinces);
    };

    const init = async () => {
        await populateProvinces();
    };

    // componentDidMount equivalent, run exclusively on client-side
    useEffect(() => {
        init();
    }, []);

    const handleProvinceChange = async (provinceId: string) => {
        if (!provinceId) {
            setDistricts([]);
            setWards([]);
            form.setFieldValue("districtId", undefined);
            form.setFieldValue("wardId", undefined);
            return;
        }
        const _districts = (await addressRepo.getAllDistricts(provinceId)).map(data => data[1]);
        setDistricts(_districts);
        setWards([]);
        form.setFieldValue("districtId", undefined);
        form.setFieldValue("wardId", undefined);
    };

    const handleDistrictChange = async (districtId: string) => {
        if (!districtId) {
            setWards([]);
            form.setFieldValue("wardId", undefined);
            return;
        }
        const _wards = (await addressRepo.getAllWards(districtId)).map(data => data[1]);
        setWards(_wards);
        form.setFieldValue("wardId", undefined);
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            const formData = await form.validateFields();
            // Ensure longitude and workingHours are included even if undefined
            const submitData: PaymentPointModel = {
                ...data,
                ...formData,
                longtitude: formData.longtitude ?? data.longtitude ?? undefined,
                workingHours: formData.workingHours ?? data.workingHours ?? undefined,
            };
            if (isEdit) {
                submitData.id = data.id;
                const result = await paymentPointRepo.update(submitData);
                setIsModalOpen(false);
                if (result) {
                    messageApi.success({
                        content: "Đã cập nhật điểm thanh toán"
                    });
                } else {
                    messageApi.error({
                        content: "Cập nhật điểm thanh toán thất bại"
                    });
                }
            } else {
                const result = await paymentPointRepo.create(submitData);
                setIsModalOpen(false);
                if (result) {
                    messageApi.success({
                        content: "Thêm điểm thanh toán mới thành công"
                    });
                } else {
                    messageApi.error({
                        content: "Thêm điểm thanh toán mới thất bại"
                    });
                }
            }
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    };

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    return (
        <ConfigProvider theme={MODAL_THEME}>
            {contextHolder}
            <Modal 
                open={isModalOpen} 
                onCancel={handleCancel} 
                width={980} 
                centered
                styles={{
                    mask: {
                        backdropFilter: 'blur(2px)',
                        backgroundColor: 'var(--primary-mask)'
                    }
                }}
                footer={[
                    <div key={1} className="w-full flex flex-row justify-end px-1.5">
                        <SaveButton isEdit={isEdit} loading={loading} onClick={handleSave}/>
                    </div>,
                ]}
            >
                <div className="flex flex-row w-full items-center justify-start select-none">
                    <p className="ml-2 text-2xl font-bold text-primary">CHI TIẾT ĐIỂM THANH TOÁN</p>
                </div>
                <div className="flex flex-row justify-center mt-6">
                    <Form<PaymentPointModel>
                        name="basic"
                        className="flex flex-col items-end"
                        form={form}
                        layout="vertical"
                        disabled={isEdit && !permissionStore.page.edit}
                    >
                        <div className="grid grid-cols-2 gap-x-4 items-end">
                            <Form.Item<PaymentPointModel>
                                label="Tên điểm thanh toán"
                                name="name"
                                rules={[{ required: true, message: 'Vui lòng nhập tên điểm thanh toán!' }]}
                            >
                                <Input placeholder="Nhập tên điểm thanh toán" style={{width: INPUT_WIDTH}}></Input>
                            </Form.Item>
                            <Form.Item<PaymentPointModel>
                                label="Số điện thoại"
                                name="phone"
                            >
                                <Input placeholder="Nhập số điện thoại" style={{width: INPUT_WIDTH}}></Input>
                            </Form.Item>
                            <Form.Item<PaymentPointModel>
                                label="Địa chỉ"
                                name="address"
                                rules={[{ required: true, message: 'Vui lòng nhập địa chỉ!' }]}
                            >
                                <Input placeholder="Nhập địa chỉ" style={{width: INPUT_WIDTH}}></Input>
                            </Form.Item>
                            <Form.Item<PaymentPointModel>
                                label="Trạng thái"
                                name="status"
                                rules={[{ required: true, message: 'Vui lòng chọn trạng thái!' }]}
                            >
                                <Select placeholder="Chọn trạng thái" style={{width: INPUT_WIDTH}}>
                                    <Select.Option value="Active">Hoạt động</Select.Option>
                                    <Select.Option value="Inactive">Tạm ngưng</Select.Option>
                                </Select>
                            </Form.Item>
                            <Form.Item<PaymentPointModel>
                                label="Tỉnh/ TP"
                                name="provinceId"
                            >
                                <Select 
                                    placeholder="Chọn Tỉnh/ TP" 
                                    style={{width: INPUT_WIDTH}} 
                                    onChange={handleProvinceChange}
                                    allowClear
                                >
                                    {provinces.map(province => (
                                        <Select.Option key={province.id} value={province.id}>
                                            {province.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item<PaymentPointModel>
                                label="Vĩ độ"
                                name="latitude"
                            >
                                <InputNumber 
                                    placeholder="Nhập vĩ độ" 
                                    style={{width: INPUT_WIDTH}}
                                    precision={6}
                                ></InputNumber>
                            </Form.Item>
                            <Form.Item<PaymentPointModel>
                                label="Quận/ Huyện"
                                name="districtId"
                            >
                                <Select 
                                    placeholder="Chọn Quận/ Huyện" 
                                    style={{width: INPUT_WIDTH}} 
                                    onChange={handleDistrictChange}
                                    allowClear
                                >
                                    {districts.map(district => (
                                        <Select.Option key={district.id} value={district.id}>
                                            {district.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item<PaymentPointModel>
                                label="Kinh độ"
                                name="longtitude"
                            >
                                <InputNumber 
                                    placeholder="Nhập kinh độ" 
                                    style={{width: INPUT_WIDTH}}
                                    precision={6}
                                ></InputNumber>
                            </Form.Item>
                            <Form.Item<PaymentPointModel>
                                label="Xã/ Phường"
                                name="wardId"
                            >
                                <Select placeholder="Chọn Xã/ Phường" style={{width: INPUT_WIDTH}} allowClear>
                                    {wards.map(ward => (
                                        <Select.Option key={ward.id} value={ward.id}>
                                            {ward.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item<PaymentPointModel>
                                label="Mô tả"
                                name="description"
                            >
                                <Input.TextArea 
                                    placeholder="Nhập mô tả" 
                                    style={{width: INPUT_WIDTH}}
                                    rows={3}
                                ></Input.TextArea>
                            </Form.Item>
                            <Form.Item<PaymentPointModel>
                                label="Giờ làm việc"
                                name="workingHours"
                            >
                                <Input 
                                    placeholder="Nhập giờ làm việc (VD: 8:00 - 17:00)" 
                                    style={{width: INPUT_WIDTH}}
                                ></Input>
                            </Form.Item>
                        </div>
                    </Form>
                </div>
                <div className="mb-5 mx-2 border-t border-dashed border-gray-300"></div>
            </Modal>
        </ConfigProvider>
    );
};

